import * as React from "react";
import { useEffect, useState } from "react";
import {
  Select,
  TextField,
  MenuItem,
  CircularProgress,
  Box,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { Card, CardActions, Container } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { Button, Typography } from "@mui/material";
import { AddNewRiskFormValues } from "../../modal";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createCyberSecurityRiskAssessment,
  updateCyberSecurityRiskAssessment,
  fetchDropdownList,
} from "../../helper";
import { useUserContext } from "../../../../context/auth/UserContext";
import { useRiskAssessmentContext } from "../../../../context/RiskAssessment/RiskAssessmentContext";
import DialogBoxComponent from "../../../../components/DialogBoxComponent";
import { getInherentRisk } from "../../utils";
import { useAccessRights } from "../../../../utils/useAccessRights";

const getTypographyStyles = () => ({
  //Typography styles
  fontWeight: 400,
  fontSize: "14px",
  fontStyle: "italic",
  opacity: 0.6,
});

const initialFormValues: AddNewRiskFormValues = {
  threatSource: 0,
  threatEvent: [],
  vulnerability: [],
  likelihood: 0,
  adverseImpact: 0,
  inherentRisk: 0,
  control: [],
  residualRisk: 0,
  riskResponse: 0,
  comment: "",
};

export default function CSRiskAssesmentForm() {
  const riskAssesment_Cs = useAccessRights("Risk Assessment - CS")

  const navigate = useNavigate();
  const location = useLocation();
  const { action } = location.state;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formTitle, setFormTitle] = useState<string>("Create Risk");
  const [formValues, setFormValues] =
    useState<AddNewRiskFormValues>(initialFormValues);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const {
    selectedCyberSecurityRiskAssessment,
    threatSourceList,
    setThreatSourceList,
    likehoodList,
    setLikehoodList,
    adverseImpactList,
    setAdverseImpactList,
    riskResponseList,
    setRiskResponseList,
    inherentRisksList,
    setInherentRisksList,
    vulnerabilityList,
    setVulnerabilityList,
    residualRiskList,
    setResidualRiskList,
    controlsList,
    setControlsList,
    threatEventList,
    setThreatEventList,
    cyberRiskAssessmentList,
  } = useRiskAssessmentContext();

  const handleCancelForm = () => {
    navigate("/risk-assessment");
    setFormValues(initialFormValues);
    setLikehoodList([]);
    setThreatSourceList([]);
    setAdverseImpactList([]);
    setRiskResponseList([]);
    setVulnerabilityList([]);
    setInherentRisksList([]);
    setResidualRiskList([]);
    setControlsList([]);
    setThreatEventList([]);
  };

  const handleDialogCancel = () => {
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
    });
    if (dialogState.title === "Success") {
      handleCancelForm();
    }
  };

  const handleChange = (event: { target: { name: any; value: any } }) => {
    const { name, value } = event.target;
    setFormValues((prevValues: any) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const disableForm = () => {
    if (
      !formValues.threatSource ||
      !formValues.threatEvent ||
      !formValues.vulnerability ||
      !formValues.likelihood ||
      !formValues.adverseImpact ||
      !formValues.inherentRisk ||
      !formValues.control ||
      !formValues.residualRisk ||
      !formValues.riskResponse
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleCreateAssessment = async () => {
    const isExisting = cyberRiskAssessmentList.filter((ele) => {
      const threatID = threatSourceList.find(
        (item: any) => item.threatName === ele.threatSourceName
      )?.threatID;
      return threatID === formValues.threatSource;
    });

    if (isExisting.length > 0) {
      setDialogState({
        title: "Info",
        body: "Risk with Threat Source already exists",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
      return;
    }

    const date = new Date();
    const payload = {
      vulnerabilityId: formValues.vulnerability.reduce(
        (total, ele) => `${total},${ele}`,
        ""
      ),
      controlId: formValues.control.reduce(
        (total, ele) => `${total},${ele}`,
        ""
      ),
      threatEventId: formValues.threatEvent.reduce(
        (total, ele) => `${total},${ele}`,
        ""
      ),
      threatSourceId: formValues.threatSource,
      likelihoodId: formValues.likelihood,
      adverseImpactId: formValues.adverseImpact,
      inherentRiskId: formValues.inherentRisk,
      residualRiskId: formValues.residualRisk,
      responseId: formValues.riskResponse,
      comment: formValues.comment,
      companyProfileID: companyProfileID,
      createDate: date,
      createdBy: userId,
    };
    const res = await createCyberSecurityRiskAssessment(payload);
    if (res.succeeded) {
      setDialogState({
        title: "Success",
        body: "Risk successfully created",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    }
  };

  const handleEditAssessment = async () => {
    const isExisting = cyberRiskAssessmentList.filter((ele) => {
      const threatID = threatSourceList.find(
        (item: any) => item.threatName === ele.threatSourceName
      )?.threatID;
      return (
        threatID === formValues.threatSource &&
        ele.assessmentID !== selectedCyberSecurityRiskAssessment.assessmentID
      );
    });

    if (isExisting.length > 0) {
      setDialogState({
        title: "Info",
        body: "Risk with Threat Source already exists",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
      return;
    }

    const date = new Date();
    const payload = {
      vulnerabilityId: formValues.vulnerability.reduce(
        (total, ele) => `${total},${ele}`,
        ""
      ),
      controlId: formValues.control.reduce(
        (total, ele) => `${total},${ele}`,
        ""
      ),
      threatEventId: formValues.threatEvent.reduce(
        (total, ele) => `${total},${ele}`,
        ""
      ),
      threatSourceId: formValues.threatSource,
      likelihoodId: formValues.likelihood,
      adverseImpactId: formValues.adverseImpact,
      inherentRiskId: formValues.inherentRisk,
      residualRiskId: formValues.residualRisk,
      responseId: formValues.riskResponse,
      comment: formValues.comment,
      updateDate: date,
      updatedBy: userId,
      assessmentID: selectedCyberSecurityRiskAssessment?.assessmentID,
      companyProfileID: companyProfileID,
    };
    const res = await updateCyberSecurityRiskAssessment(payload);
    if (res.succeeded) {
      setDialogState({
        title: "Success",
        body: "Risk successfully updated",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
    }
  };

  useEffect(() => {
    if (formValues.likelihood && formValues.adverseImpact) {
      const inherentRiskName = getInherentRisk(
        formValues,
        likehoodList,
        adverseImpactList
      );

      const inherentRisk =
        inherentRisksList.find(
          (item) => item.inherentRiskName === inherentRiskName
        )?.inherentRiskID || 0;
      // console.log("getInherentRisk inherentRisk", inherentRisk);

      setFormValues((prevValues: any) => ({
        ...prevValues,
        inherentRisk,
      }));
    }
  }, [formValues.likelihood, formValues.adverseImpact]);

  const fetchOptions = async () => {
    setIsLoading(true);

    const dropdown = await fetchDropdownList(companyProfileID);
    setAdverseImpactList(dropdown[0].adverseImpacts);
    setInherentRisksList(dropdown[0].inherentRisks);
    setLikehoodList(dropdown[0].likelihoods);
    setResidualRiskList(dropdown[0].residualRisks);
    setRiskResponseList(dropdown[0].responses);
    setThreatEventList(dropdown[0].threatEvents);
    setThreatSourceList(dropdown[0].threatSources);
    setVulnerabilityList(dropdown[0].vulnerabilities);
    setControlsList(dropdown[0].controls);

    if (selectedCyberSecurityRiskAssessment?.assessmentID) {
      setFormTitle("Edit Risk");
      setFormValues({
        threatEvent: selectedCyberSecurityRiskAssessment.threatEvents.map(
          (ele: { threatEventID: any }) => ele.threatEventID
        ),
        vulnerability: selectedCyberSecurityRiskAssessment.vulnerabilites.map(
          (ele: { vulnerabilityId: any }) => ele.vulnerabilityId
        ),
        control: selectedCyberSecurityRiskAssessment.controls.map(
          (ele: { controlsID: any }) => ele.controlsID
        ),
        threatSource: selectedCyberSecurityRiskAssessment.threatSourceId,
        likelihood: selectedCyberSecurityRiskAssessment.likelihoodId,
        adverseImpact: selectedCyberSecurityRiskAssessment.adverseImpactId,
        inherentRisk: selectedCyberSecurityRiskAssessment.inherentRiskId,
        residualRisk: selectedCyberSecurityRiskAssessment.residualRiskId,
        riskResponse: selectedCyberSecurityRiskAssessment.responseId,
        comment: selectedCyberSecurityRiskAssessment.comment,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  return (
    <React.Fragment>
      <Card sx={{ borderRadius: "10px", height: "100%", my: 2, p: 3 }}>
        <div>
          {" "}
          <Typography
            sx={{
              fontWeight: 200,
              opacity: 0.9,
            }}
          >
            {formTitle}
          </Typography>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Container
              sx={{
                mt: 1,
                height: "90%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid
                sx={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  m: 0,
                }}
                container
                columnSpacing={2}
                rowSpacing={1}
              >
                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>
                    Threat source:
                  </Typography>
                  <Select
                    fullWidth
                    name="threatSource"
                    value={formValues.threatSource}
                    onChange={handleChange}
                  >
                    {threatSourceList
                      .sort((a, b) => a.threatName.localeCompare(b.threatName))
                      .map((ele: any) => (
                        <MenuItem key={ele.threatID} value={ele.threatID}>
                          {ele.threatName}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>Threat Event:</Typography>
                  <Select
                    fullWidth
                    multiple
                    name="threatEvent"
                    value={formValues.threatEvent}
                    onChange={handleChange}
                    renderValue={(selected: any) =>
                      threatEventList
                        .filter((ele: any) => selected.includes(ele.threatEventID))
                        .map((ele: any) => ele.threatEventName)
                        .join(", ")
                    }
                  >
                    {threatEventList
                      .sort((a, b) => a.threatEventName.localeCompare(b.threatEventName))
                      .map((ele: any) => (
                        <MenuItem key={ele.threatEventID} value={ele.threatEventID}>
                          <Checkbox
                            checked={formValues.threatEvent.indexOf(ele.threatEventID) > -1}
                          />
                          <ListItemText primary={ele.threatEventName} />
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>Vulnerability:</Typography>
                  <Select
                    fullWidth
                    multiple
                    name="vulnerability"
                    value={formValues.vulnerability}
                    onChange={handleChange}
                    renderValue={(selected: any) =>
                      vulnerabilityList
                        .filter((ele: any) => selected.includes(ele.vulnerabilityId))
                        .map((ele: any) => ele.vulnerabiliteName)
                        .join(", ")
                    }
                  >
                    {vulnerabilityList
                      .sort((a, b) => a.vulnerabiliteName.localeCompare(b.vulnerabiliteName))
                      .map((ele: any) => (
                        <MenuItem key={ele.vulnerabilityId} value={ele.vulnerabilityId}>
                          <Checkbox
                            checked={formValues.vulnerability.indexOf(ele.vulnerabilityId) > -1}
                          />
                          <ListItemText primary={ele.vulnerabiliteName} />
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>Likelihood:</Typography>
                  <Select
                    fullWidth
                    name="likelihood"
                    value={formValues.likelihood}
                    onChange={handleChange}
                  >
                    {likehoodList
                      .sort((a, b) =>
                        a.likelihoodName.localeCompare(b.likelihoodName)
                      )
                      .map((ele: any) => (
                        <MenuItem
                          key={ele.likelihoodID}
                          value={ele.likelihoodID}
                        >
                          {ele.likelihoodName}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>
                    Adverse Impact:
                  </Typography>
                  <Select
                    fullWidth
                    name="adverseImpact"
                    value={formValues.adverseImpact}
                    onChange={handleChange}
                  >
                    {adverseImpactList
                      .sort((a, b) =>
                        a.adverseImpactName.localeCompare(b.adverseImpactName)
                      )
                      .map((ele: any) => (
                        <MenuItem
                          key={ele.adverseImpactID}
                          value={ele.adverseImpactID}
                        >
                          {ele.adverseImpactName}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>
                    Inherent Risk:
                  </Typography>
                  <Select
                    fullWidth
                    readOnly
                    name="inherentRisk"
                    value={formValues.inherentRisk}
                  >
                    {inherentRisksList
                      .sort((a, b) =>
                        a.inherentRiskName.localeCompare(b.inherentRiskName)
                      )
                      .map((ele: any) => (
                        <MenuItem
                          key={ele.inherentRiskID}
                          value={ele.inherentRiskID}
                        >
                          {ele.inherentRiskName}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>
                    Residual Risk:
                  </Typography>
                  <Select
                    fullWidth
                    name="residualRisk"
                    value={formValues.residualRisk}
                    onChange={handleChange}
                  >
                    {residualRiskList
                      .sort((a, b) =>
                        a.residualRiskName.localeCompare(b.residualRiskName)
                      )
                      .map((ele: any) => (
                        <MenuItem
                          key={ele.residualRiskID}
                          value={ele.residualRiskID}
                        >
                          {ele.residualRiskName}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>Control:</Typography>
                  <Select
                    fullWidth
                    multiple
                    name="control"
                    value={formValues.control}
                    onChange={handleChange}
                    renderValue={(selected: any) =>
                      controlsList
                        .filter((ele: any) => selected.includes(ele.controlsID))
                        .map((ele: any) => ele.controlsName)
                        .join(", ")
                    }
                  >
                    {controlsList
                      .sort((a, b) => a.controlsName.localeCompare(b.controlsName))
                      .map((ele: any) => (
                        <MenuItem key={ele.controlsID} value={ele.controlsID}>
                          <Checkbox checked={formValues.control.indexOf(ele.controlsID) > -1} />
                          <ListItemText primary={ele.controlsName} />
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>


                <Grid xs={12} sm={4}>
                  <Typography sx={getTypographyStyles}>
                    Risk Response:
                  </Typography>
                  <Select
                    fullWidth
                    name="riskResponse"
                    value={formValues.riskResponse}
                    onChange={handleChange}
                  >
                    {riskResponseList
                      .sort((a, b) =>
                        a.responseName.localeCompare(b.responseName)
                      )
                      .map((ele: any) => (
                        <MenuItem key={ele.responseID} value={ele.responseID}>
                          {ele.responseName}
                        </MenuItem>
                      ))}
                  </Select>
                </Grid>

                <Grid xs={12}>
                  <Typography sx={getTypographyStyles}>Comment:</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="comment"
                    value={formValues.comment}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <CardActions
                sx={{
                  justifyContent: "end",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#0066FF1A",
                    color: "#0066FF",
                    "&:hover": {
                      bgcolor: "#0066FF1A",
                      "& .MuiTypography-root": {
                        color: "white",
                      },
                    },
                    boxShadow: 0,
                    textTransform: "none",
                  }}
                  onClick={handleCancelForm}
                >
                  Cancel
                </Button>
                {action === "Edit" ? (
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      boxShadow: 0,
                      backgroundColor: "#0066FF",
                    }}
                    disabled={disableForm() || !riskAssesment_Cs?.isUpdate}
                    onClick={handleEditAssessment}
                  >
                    Edit
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      boxShadow: 0,
                      backgroundColor: "#0066FF",
                    }}
                    disabled={disableForm() || !riskAssesment_Cs?.isCreate}
                    onClick={handleCreateAssessment}
                  >
                    Create
                  </Button>
                )}
              </CardActions>
            </Container>
          )}
        </div>
      </Card>
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleDialogCancel}
        />
      }
    </React.Fragment>
  );
}
